/**
 * The day arc — which *face* the board wears at a given wall-clock moment.
 *
 * Locked by surfaces-and-rhythm (product ticket 04) and CONTEXT.md
 * "Invite state":
 *
 *   day     … the normal board (spotlight + cascade), incl. the morning
 *             compose hostage and the Mode-on stripped view, which are
 *             *content* states owned by the board skeleton, not the arc.
 *   invite  … 22:00–01:00. The task machinery fades out and the Directions'
 *             imagery goes full-bleed — the invite verb given the whole
 *             screen. A **resting face, not a lockout**: one interaction
 *             brings the board (and its Spotlight) straight back.
 *   night   … 01:00 onward. Near-black; the monitor's own display sleep does
 *             the actual power-down. No wake machinery, no smart plug.
 *
 * This module is pure on purpose — the arc is the one piece of day-arc
 * behaviour worth unit-testing, and it must be reasonable to reason about at
 * 01:00 with no browser attached. Nothing here touches the DOM or the clock;
 * `now` is always passed in.
 */

export type DayPhase = 'day' | 'invite' | 'night';

/** What actually gets rendered — the phase after interaction is accounted for. */
export type BoardFace = 'board' | 'invite' | 'night';

export const ARC = {
  /** local hour the invite state begins. */
  inviteHour: 22,
  /** local hour the board goes near-black. */
  nightHour: 1,
  /**
   * Local hour the ordinary board returns. Not stated by the product ticket
   * (it only says "display sleep does the rest"); taken from the freshness
   * decision, whose poll resumes at 06:00 — the board should not be dark
   * while it is polling. Conservative call, flagged in the ticket.
   */
  morningHour: 6,
  /**
   * How long one interaction holds the board open before it settles back to
   * its resting face. Long enough to tick the last item off the plan, short
   * enough that a passing cat does not light the room all night.
   * TODO(Kevin): 90s is a taste call, not a decision.
   */
  revealMs: 90_000,
} as const;

const HOUR_MS = 60 * 60_000;
const DAY_MS = 24 * HOUR_MS;

/** The time-of-day phase alone, ignoring any interaction. */
export function dayPhase(now: Date = new Date()): DayPhase {
  const h = now.getHours();
  if (h >= ARC.nightHour && h < ARC.morningHour) return 'night';
  if (h >= ARC.inviteHour || h < ARC.nightHour) return 'invite';
  return 'day';
}

export interface ArcInput {
  now: Date;
  /**
   * Epoch ms of the last wake interaction (click / tap / key / wheel), or
   * null if the board has not been touched since load.
   */
  lastWakeAt: number | null;
  /**
   * Mode is on. Mode is an explicit act with a running clock; fading it to
   * imagery at 22:00 would be a lockout of exactly the kind the decision
   * rules out, so the arc yields to it.
   */
  modeOn: boolean;
  /** The compose sheet is open — an interaction already in progress. */
  composing: boolean;
}

export interface ArcState {
  phase: DayPhase;
  face: BoardFace;
  /** true when an interaction (not the hour) is what is holding the board open. */
  revealed: boolean;
  /** ms left on the reveal, or null when nothing is counting down. */
  revealExpiresInMs: number | null;
}

export function computeArc(i: ArcInput): ArcState {
  const phase = dayPhase(i.now);
  if (phase === 'day') {
    return { phase, face: 'board', revealed: false, revealExpiresInMs: null };
  }

  // The arc never interrupts something running. Mode-on and an open compose
  // sheet keep the board up whatever the hour.
  if (i.modeOn || i.composing) {
    return { phase, face: 'board', revealed: false, revealExpiresInMs: null };
  }

  const left = revealLeftMs(i.now, i.lastWakeAt);
  if (left > 0) {
    return { phase, face: 'board', revealed: true, revealExpiresInMs: left };
  }
  return { phase, face: phase, revealed: false, revealExpiresInMs: null };
}

/**
 * ms remaining on the reveal window. A wake stamp in the future (clock jump,
 * a kiosk waking from sleep with a corrected clock) is clamped to "just now"
 * rather than granting an unbounded reveal.
 */
export function revealLeftMs(now: Date, lastWakeAt: number | null): number {
  if (lastWakeAt === null) return 0;
  const elapsed = Math.max(0, now.getTime() - lastWakeAt);
  return Math.max(0, ARC.revealMs - elapsed);
}

/**
 * ms until the arc could next change on its own — the sooner of the reveal
 * lapsing and the next phase boundary. Callers use it to fade back at the
 * right moment instead of polling the clock. Capped at an hour so a sleeping
 * kiosk re-checks periodically even if a timer drifted.
 */
export function msUntilNextArcEvent(i: ArcInput): number {
  let best = Math.min(msUntilNextBoundary(i.now), HOUR_MS);
  const left = revealLeftMs(i.now, i.lastWakeAt);
  if (left > 0) best = Math.min(best, left);
  // Never hand back a busy-loop.
  return Math.max(250, best);
}

/** ms from `now` to the next of the three arc boundaries (local hours). */
export function msUntilNextBoundary(now: Date): number {
  const ms =
    now.getHours() * HOUR_MS +
    now.getMinutes() * 60_000 +
    now.getSeconds() * 1000 +
    now.getMilliseconds();
  let best = DAY_MS;
  for (const h of [ARC.nightHour, ARC.morningHour, ARC.inviteHour]) {
    const delta = h * HOUR_MS - ms;
    best = Math.min(best, delta > 0 ? delta : delta + DAY_MS);
  }
  return best;
}
